import { RadioCard, Tooltip, TooltipContent, TooltipRoot, TooltipProvider } from "@blurb/codex-react";
import { Box, createStyles } from "@mantine/core";
import { RpiLabelText } from "@rpi/openapi-core";
import { getCardIconPath, getDisplayName } from "./attributeValueMappings";

export type IconCardConfig = {
  value: string;
  displayName: string;
  iconPath: string;
  disabled?: boolean;
  renderTooltip?: () => JSX.Element;
};

type IconCardProps = IconCardConfig;

const useIconCardStyles = createStyles((theme) => ({
  radioCard: {
    width: 96,
    height: 128,
    borderRadius: theme.radius.sm,
    justifyContent: "flex-start",
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    padding: `${theme.spacing.xs}px ${theme.spacing.xs}px`,
    border: `1px solid ${theme.other.semanticColors.border.subtle}`,
  },
  cardContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: theme.spacing.xs,
  },
  imageContainer: {
    borderRadius: theme.radius.sm,
    background: theme.other.semanticColors.bg.graySubtlest,
    padding: 17.6,
    justifyContent: "center",
    alignItems: "flex-end",
    display: "flex",
    height: 88,
    width: 88,
  },
  labelContainer: {
    marginTop: theme.spacing.xs,
    textAlign: "center",
    width: "100%",
    "& *": {
      fontSize: `${theme.fontSizes.sm}px !important`,
    },
  },
}));

export const defaultGetIconCardConfig =
  (attributeKey: string): ((value: string) => IconCardConfig | undefined) =>
  (value) => {
    if (!attributeKey) return undefined;

    const iconPath = getCardIconPath(attributeKey, String(value));
    const displayName = getDisplayName(attributeKey, String(value));

    if (!iconPath || !displayName) return undefined;

    return {
      value: String(value),
      displayName,
      iconPath,
      disabled: false,
    };
  };

export const IconCard = ({ value, displayName, iconPath, disabled = false, renderTooltip }: IconCardProps) => {
  const { classes } = useIconCardStyles();

  const card = (
    <RadioCard className={classes.radioCard} value={value} disabled={disabled}>
      <Box className={classes.cardContent}>
        <Box className={classes.imageContainer}>
          <img src={iconPath} alt={`${displayName}`} />
        </Box>
      </Box>
      <Box className={classes.labelContainer}>
        <RpiLabelText>{displayName}</RpiLabelText>
      </Box>
    </RadioCard>
  );

  if (renderTooltip) {
    return (
      <TooltipProvider>
        <TooltipRoot>
          <Tooltip content={<TooltipContent>{renderTooltip()}</TooltipContent>}>
            <span style={{ display: "inline-block" }}>{card}</span>
          </Tooltip>
        </TooltipRoot>
      </TooltipProvider>
    );
  }

  return card;
};
